import { type CanvasProps, Canvas } from '@react-three/fiber';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDisableStore } from 'state/disableStore';
import { useGlobalState } from 'state/globalState';

import S from './ClientCanvas.module.scss';

import { usePathname } from 'hooks/navigation';

export interface ClientCanvasProps extends CanvasProps {
  isOverLayout?: boolean;
}

function ClientCanvas({
  children,
  className,
  isOverLayout,
  ...rest
}: ClientCanvasProps) {
  const pathname = usePathname();
  const isExperienceDisabled = useDisableStore(s => s.isExperienceDisabled);

  const [key, setKey] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const handleContextLost = useCallback((event: Event) => {
    event.preventDefault();
    console.error('WebGL context lost. Restarting canvas.');
    setKey(prevKey => prevKey + 1);
  }, []);

  const handleContextRestored = useCallback(() => {
    console.log('WebGL context restored.');
  }, []);

  const handleCreated = useCallback((state: any) => {
    const canvas = state.gl.domElement;
    canvasRef.current = canvas;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const contextLostHandler = (event: Event) => handleContextLost(event);
    const contextRestoredHandler = () => handleContextRestored();

    if (pathname === '/' || pathname === '/landing') {
      canvas.addEventListener('webglcontextlost', contextLostHandler, false);
      canvas.addEventListener(
        'webglcontextrestored',
        contextRestoredHandler,
        false
      );
    }

    return () => {
      canvas.removeEventListener('webglcontextlost', contextLostHandler, false);
      canvas.removeEventListener(
        'webglcontextrestored',
        contextRestoredHandler,
        false
      );
    };
  }, [pathname, handleContextLost, handleContextRestored]);

  const shiftCanvas = useGlobalState(state => state.shiftCanvas);

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{
        opacity: isExperienceDisabled ? 0 : 1,
        y: shiftCanvas ? -96 : 0,
      }}
      transition={{ duration: 1, delay: 0.3 }}
      className={clsx(
        className,
        isOverLayout && S.CanvasOverLayout,
        S.CanvasWrapper
      )}
    >
      <Canvas
        key={key}
        // Stays mounted while the experience is disabled so the WebGL context
        // and scene survive the route change; pausing the loop keeps it from
        // rendering a scene nobody can see.
        frameloop={isExperienceDisabled ? 'never' : 'always'}
        camera={{ near: 0.001, far: 5000 }}
        gl={{
          powerPreference: 'high-performance',
          antialias: true,
        }}
        dpr={[1, 2]}
        onCreated={handleCreated}
        {...rest}
      >
        {children}
      </Canvas>
    </motion.section>
  );
}
export default ClientCanvas;
